import { equals2 } from "fxjs/es";
import { UtilNS } from "../../../../Common/public/Util/UtilNS.js";
import { DATA_KEY_IS_INIT_TRANSFORM } from "./const.js";
import { getDet } from "../private/getDet.js";
export class TransformManipulator {
  constructor({ svg_el }) {
    this._svg_el = svg_el;
  }
  getIsInitTransform(el) {
    return equals2(el?.dataset?.[DATA_KEY_IS_INIT_TRANSFORM])("true");
  }
  initTransform(el) {
    if (this.getIsInitTransform(el)) {
      return;
    }
    const transform_list = el.transform.baseVal;

    // fixed transform
    const consolidated_transform =
      transform_list.consolidate() ||
      this._svg_el.createSVGTransformFromMatrix(this._svg_el.createSVGMatrix());
    transform_list.initialize(consolidated_transform);

    // extended transform before base transform
    transform_list.insertItemBefore(
      this._svg_el.createSVGTransformFromMatrix(this._svg_el.createSVGMatrix()),
      0
    );

    // base transform
    transform_list.insertItemBefore(
      this._svg_el.createSVGTransformFromMatrix(this._svg_el.createSVGMatrix()),
      0
    );

    // extended transform after base transform
    transform_list.insertItemBefore(
      this._svg_el.createSVGTransformFromMatrix(this._svg_el.createSVGMatrix()),
      0
    );
    el.dataset[DATA_KEY_IS_INIT_TRANSFORM] = "true";
  }
  getFixedTransform(el) {
    if (!this.getIsInitTransform(el)) {
      return;
    }
    return UtilNS.tryNoop(() => el?.transform?.baseVal?.getItem?.(3));
  }
  getBeforeBaseTransform(el) {
    if (!this.getIsInitTransform(el)) {
      return;
    }
    return UtilNS.tryNoop(() => el?.transform?.baseVal?.getItem?.(2));
  }
  getBaseTransform(el) {
    if (!this.getIsInitTransform(el)) {
      return;
    }
    return UtilNS.tryNoop(() => el?.transform?.baseVal?.getItem?.(1));
  }
  getAfterBaseTransform(el) {
    if (!this.getIsInitTransform(el)) {
      return;
    }
    return UtilNS.tryNoop(() => el?.transform?.baseVal?.getItem?.(0));
  }
  setFixedTransform(matrix, el) {
    if (!this.getIsInitTransform(el)) {
      throw new Error(`초기화하지 않은 엘리먼트입니다.`);
    }
    const det = getDet(matrix);
    if (Number.isNaN(det) || !Number.isFinite(det) || det === 0) {
      throw new Error(`유효하지 않은 matrix 입니다.`);
    }
    el.transform.baseVal.getItem(3).setMatrix(matrix);
  }
  setExtendedTransformBeforeBase(matrix, el) {
    if (!this.getIsInitTransform(el)) {
      throw new Error(`초기화하지 않은 엘리먼트입니다.`);
    }
    const det = getDet(matrix);
    if (Number.isNaN(det) || !Number.isFinite(det) || det === 0) {
      throw new Error(`유효하지 않은 matrix 입니다.`);
    }
    el.transform.baseVal.getItem(2).setMatrix(matrix);
  }
  setBaseTransform(matrix, el) {
    if (!this.getIsInitTransform(el)) {
      throw new Error(`초기화하지 않은 엘리먼트입니다.`);
    }
    const det = getDet(matrix);
    if (Number.isNaN(det) || !Number.isFinite(det) || det === 0) {
      throw new Error(`유효하지 않은 matrix 입니다.`);
    }
    el.transform.baseVal.getItem(1).setMatrix(matrix);
  }
  setExtendedTransformAfterBase(matrix, el) {
    if (!this.getIsInitTransform(el)) {
      throw new Error(`초기화하지 않은 엘리먼트입니다.`);
    }
    const det = getDet(matrix);
    if (Number.isNaN(det) || !Number.isFinite(det) || det === 0) {
      throw new Error(`유효하지 않은 matrix 입니다.`);
    }
    el.transform.baseVal.getItem(0).setMatrix(matrix);
  }
  mergeExtendedTransformToFixed(el) {
    if (!this.getIsInitTransform(el)) {
      throw new Error(`초기화하지 않은 엘리먼트입니다.`);
    }
    const extended_transform_after_base_transform =
      this.getAfterBaseTransform(el);
    if (extended_transform_after_base_transform == null) {
      throw new Error(`엘리먼트의 after_base_transform 을 찾을 수 없습니다.`);
    }
    const { matrix: extended_transform_after_base_transform_matrix } =
      extended_transform_after_base_transform;
    const base_transform = this.getBaseTransform(el);
    if (base_transform == null) {
      throw new Error(`엘리먼트의 base_transform 을 찾을 수 없습니다.`);
    }
    const { matrix: base_transform_matrix } = base_transform;
    const extended_transform_before_base_transform =
      this.getBeforeBaseTransform(el);
    if (extended_transform_before_base_transform == null) {
      throw new Error(`엘리먼트의 before_base_transform 을 찾을 수 없습니다.`);
    }
    const { matrix: extended_transform_before_base_transform_matrix } =
      extended_transform_before_base_transform;
    const fixed_transform = this.getFixedTransform(el);
    if (fixed_transform == null) {
      throw new Error(`엘리먼트의 fixed_transform 을 찾을 수 없습니다.`);
    }
    const { matrix: fixed_transform_matrix } = fixed_transform;
    const matrix = extended_transform_after_base_transform_matrix
      .multiply(base_transform_matrix)
      .multiply(extended_transform_before_base_transform_matrix)
      .multiply(fixed_transform_matrix);
    this.setFixedTransform(matrix, el);
    this.setExtendedTransformBeforeBase(this._svg_el.createSVGMatrix(), el);
    this.setBaseTransform(this._svg_el.createSVGMatrix(), el);
    this.setExtendedTransformAfterBase(this._svg_el.createSVGMatrix(), el);
  }
  mergeExtendedTransformToBase(el) {
    if (!this.getIsInitTransform(el)) {
      throw new Error(`초기화하지 않은 엘리먼트입니다.`);
    }
    const extended_transform_after_base_transform =
      this.getAfterBaseTransform(el);
    if (extended_transform_after_base_transform == null) {
      throw new Error(`엘리먼트의 after_base_transform 을 찾을 수 없습니다.`);
    }
    const { matrix: extended_transform_after_base_transform_matrix } =
      extended_transform_after_base_transform;
    const base_transform = this.getBaseTransform(el);
    if (base_transform == null) {
      throw new Error(`엘리먼트의 base_transform 을 찾을 수 없습니다.`);
    }
    const { matrix: base_transform_matrix } = base_transform;
    const extended_transform_before_base_transform =
      this.getBeforeBaseTransform(el);
    if (extended_transform_before_base_transform == null) {
      throw new Error(`엘리먼트의 before_base_transform 을 찾을 수 없습니다.`);
    }
    const { matrix: extended_transform_before_base_transform_matrix } =
      extended_transform_before_base_transform;
    const matrix = extended_transform_after_base_transform_matrix
      .multiply(base_transform_matrix)
      .multiply(extended_transform_before_base_transform_matrix);
    this.setExtendedTransformBeforeBase(this._svg_el.createSVGMatrix(), el);
    this.setBaseTransform(matrix, el);
    this.setExtendedTransformAfterBase(this._svg_el.createSVGMatrix(), el);
  }
}
